/**
 * Color system for the EVO application
 * 
 * Usage Examples (from SignIn.tsx):
 * 
 * 1. Page Background:
 *    background: `radial-gradient(circle at center, ${colors.premium.dark} 0%, ${colors.premium.darker} 100%)`
 * 
 * 2. Card Styling:
 *    - Background: bg-[#1C1C1E] (premium.dark)
 *    - Border: border-gray-800/50 or use border.dark
 * 
 * 3. Form Elements:
 *    - Input Background: bg-[${colors.premium.input}]
 *    - Input Border Focus: focus:border-[${colors.border.focus}]
 * 
 * 4. Buttons:
 *    - Gradient: `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)`
 *    - Shadow: `0 8px 24px -8px ${colors.brand.primary}80`
 */

export const colors = {
  brand: {
    // Main brand colors for primary actions and gradients
    primary: '#003DA5',  // Primary blue, used in button gradients and accents
    secondary: '#BAD80A', // Secondary accent, use sparingly for highlights
    accent: '#2A78BD'    // Lighter blue for gradient ends and hover states
  },
  premium: {
    // Dark theme colors for backgrounds and surfaces
    dark: '#1C1C1E',    // Main dark background, card backgrounds
    darker: '#141416',   // Darker shade for gradients and depth
    input: '#2C2C2E',   // Form input backgrounds in dark theme
    warm: '#8B7355',    // Warm accent for special elements
    light: '#D4C4A8'    // Light accent for contrast elements
  },
  text: {
    // Typography colors
    light: '#FFFFFF',    // Primary text on dark backgrounds
    dark: '#1A1A1A',    // Primary text on light backgrounds
    medium: '#666666',   // Secondary text and labels
    placeholder: '#9CA3AF'  // Placeholder text in inputs
  },
  border: {
    // Border colors and states
    dark: 'rgba(31, 31, 31, 0.5)',  // Card and element borders
    focus: '#3B82F6'    // Focus state for interactive elements
  }
} as const;

/**
 * Common Styling Patterns:
 * 
 * 1. Dark Card with Glow:
 *    <div className="bg-[#1C1C1E] border border-gray-800/50 shadow-[0_0_50px_-12px_rgba(0,0,0,0.4)]">
 * 
 * 2. Input Field:
 *    <input className="bg-[#2C2C2E] border-gray-700 text-white placeholder-gray-400 
 *                      focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20" />
 * 
 * 3. Gradient Button:
 *    <button style={{ 
 *      background: `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)`,
 *      boxShadow: `0 8px 24px -8px ${colors.brand.primary}80`
 *    }}>
 * 
 * 4. Background Gradient:
 *    <div style={{ 
 *      background: `radial-gradient(circle at center, ${colors.premium.dark} 0%, ${colors.premium.darker} 100%)`
 *    }}>
 */
